import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ToastContainer } from 'react-toastify';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import 'react-toastify/dist/ReactToastify.css';
import './styles/global.css';

// Contexts
import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ChatProvider } from './contexts/ChatContext';
import ErrorBoundary from './components/common/ErrorBoundary';
import RoleGuard from './components/common/RoleGuard';
import SessionExpiryWatcher from './components/common/SessionExpiryWatcher';

// Layout Components
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import PrivateLayout from './components/layout/PrivateLayout';
import TechnicianLayout from './components/technician/layout/TechnicianLayout';

// Public Pages
import Home from './pages/Home';
import Services from './pages/Services';
import Industries from './pages/Industries';
// import Projects from './pages/Projects';
// import Resources from './pages/Resources';
// import Careers from './pages/Careers';
import About from './pages/About';
import Contact from './pages/Contact';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import KaneLaunch from './pages/KaneLaunch';
import OauthContinue from './pages/OauthContinue';
import NotFound from './pages/NotFound';

// Private Pages
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import Changelog from './pages/Changelog';
import HelpPage from './pages/HelpPage';
import AgenteConexion from './pages/agente/AgenteConexion';
import AgenteHandbook from './pages/agente/AgenteHandbook';
import ProjectsUnifiedView from './pages/projects/ProjectsUnifiedView';
import ProjectDetail from './pages/projects/ProjectDetail';
import ProjectForm from './pages/projects/ProjectForm';
import QuotationBuilder from './pages/quotations/QuotationBuilder';
import QuotationList from './pages/quotations/QuotationList';
import MyQuotations from './pages/quotations/MyQuotations';
import ExpenseList from './pages/expenses/ExpenseList';
import ExpenseForm from './pages/expenses/ExpenseForm';
import InventoryList from './pages/inventory/InventoryList';
import MaterialForm from './pages/inventory/MaterialForm';
import InventoryValuation from './pages/inventory/InventoryValuation';
import BatchList from './pages/inventory/BatchList';
import ToolAssignments from './pages/inventory/ToolAssignments';
import ToolAssetsManager from './pages/inventory/ToolAssetsManager';
import ProgressUpload from './pages/progress/ProgressUpload';
import PhotoGallery from './pages/progress/PhotoGallery';
import UserList from './pages/users/UserList';
import UserForm from './pages/users/UserForm';
import InvoiceListPage from './pages/invoices/InvoiceListPage';
import MessagesPage from './pages/messages/MessagesPage';

// Admin Pages
import MaterialRequestsInbox from './pages/admin/MaterialRequestsInbox';

// Client Pages
import { ClientList, ClientForm, ClientDetail } from './pages/clients';

// Technician Portal Pages
import {
  TechnicianDashboard,
  TechnicianProjects,
  TechnicianProjectDetail,
  TechnicianTasks,
  TechnicianTaskDetail,
  TechnicianMaterials,
  // TechnicianTools deprecated by #93 — route now redirects to /mis-herramientas
  MaterialRequestForm,
  SmartMaterialRequest,
  TimeEntryPage,
  TechnicianIncidents,
  IncidentReportForm,
  TechnicianMessages,
  ProjectChat,
  TechnicianProfile,
  ChecklistView,
  ChecklistDetail,
  SignatureCapture,
  SignatureList,
} from './pages/technician';

// Supplier Pages
import { SupplierList, SupplierForm } from './pages/suppliers';
import OpportunityList from './pages/opportunities/OpportunityList';
import OpportunityForm from './pages/opportunities/OpportunityForm';
import OpportunityDetail from './pages/opportunities/OpportunityDetail';

// Purchase Pages
import { PurchaseList, PurchaseForm, PurchaseDetail, AdminPOInbox } from './pages/purchases';

// RFQ Pages (Solicitudes de Cotización)
import RfqListPage from './pages/rfq/RfqListPage';
import RfqDetailPage from './pages/rfq/RfqDetailPage';
import RfqBuilder from './pages/rfq/RfqBuilder';

// Tool Management Pages (#86, #98)
import ToolList from './pages/tools/ToolList';
import ToolDetail from './pages/tools/ToolDetail';
import MyToolsPage from './pages/tools/MyToolsPage';
import MyToolRequests from './pages/tools/MyToolRequests';
import ToolRequestsInbox from './pages/tools/ToolRequestsInbox';
import WarehousePage from './pages/tools/WarehousePage';
import ToolCategoriesPage from './pages/tools/ToolCategoriesPage';
import ToolsDashboardPage from './pages/tools/ToolsDashboardPage';
import LocationsPage from './pages/admin/LocationsPage';

// Kane Atlas (admin): lazy a proposito — arrastra three.js, que no debe pesar en el bundle inicial.
const AtlasPage = lazy(() => import('./pages/admin/AtlasPage'));

// Lazy load Task pages
const TasksPage = lazy(() => import('./pages/tasks/TasksPage'));
const TaskDetailPage = lazy(() => import('./pages/tasks/TaskDetailPage'));
const TaskForm = lazy(() => import('./pages/tasks/TaskForm'));

// Lazy load the unified Calendar page (#465)
const CalendarPage = lazy(() => import('./pages/calendar/CalendarPage'));

// Loading component for Suspense
const LoadingFallback = () => (
  <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Cargando...</span>
    </div>
  </div>
);

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      cacheTime: 1000 * 60 * 10, // 10 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <NotificationProvider>
            <ChatProvider>
              {/* #389: watches AuthContext.sessionExpired and forces a clean
                  logout + redirect to /login when the session/token expires,
                  instead of leaving the app "authenticated" with silent 401s. */}
              <SessionExpiryWatcher />
              <Routes>
                {/* Public Routes */}
                <Route
                  path="/"
                  element={
                    <div className="App">
                      <Header />
                      <div className="main-content">
                        <Home />
                      </div>
                      <Footer />
                    </div>
                  }
                />
                <Route
                  path="/nosotros"
                  element={
                    <div className="App">
                      <Header />
                      <div className="main-content">
                        <About />
                      </div>
                      <Footer />
                    </div>
                  }
                />
                <Route
                  path="/servicios"
                  element={
                    <div className="App">
                      <Header />
                      <div className="main-content">
                        <Services />
                      </div>
                      <Footer />
                    </div>
                  }
                />
                <Route
                  path="/industrias"
                  element={
                    <div className="App">
                      <Header />
                      <div className="main-content">
                        <Industries />
                      </div>
                      <Footer />
                    </div>
                  }
                />
                <Route
                  path="/contacto"
                  element={
                    <div className="App">
                      <Header />
                      <div className="main-content">
                        <Contact />
                      </div>
                      <Footer />
                    </div>
                  }
                />
                <Route
                  path="/terminos"
                  element={
                    <div className="App">
                      <Header />
                      <div className="main-content">
                        <Terms />
                      </div>
                      <Footer />
                    </div>
                  }
                />
                <Route
                  path="/privacidad"
                  element={
                    <div className="App">
                      <Header />
                      <div className="main-content">
                        <Privacy />
                      </div>
                      <Footer />
                    </div>
                  }
                />
                <Route path="/login" element={<Login />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />

                {/* SP-initiated SSO entry point (#385) — Kane OneDeck sends the
                    user here to log in with their ProMedic account. Manages its
                    own auth (detours through /login when there's no session). */}
                <Route path="/kane-launch" element={<KaneLaunch />} />

                {/* OAuth connector branded-login step — a remote MCP client
                    (ChatGPT) sends the user here to sign in with their ProMedic
                    account, then bounces back to the client with an auth code.
                    Manages its own auth (detours through /login). */}
                <Route path="/oauth-continue" element={<OauthContinue />} />

                {/* Private Routes */}
                <Route path="/dashboard" element={<PrivateLayout />}>
                  <Route index element={<Dashboard />} />
                </Route>

                {/* Profile Route */}
                <Route path="/perfil" element={<PrivateLayout />}>
                  <Route index element={<Profile />} />
                </Route>

                {/* Changelog / Novedades */}
                <Route path="/novedades" element={<PrivateLayout />}>
                  <Route index element={<Changelog />} />
                </Route>

                {/* Help / Ayuda */}
                <Route path="/ayuda" element={<PrivateLayout />}>
                  <Route index element={<HelpPage />} />
                  <Route path=":moduleId" element={<HelpPage />} />
                </Route>

                {/* Tu agente: conexion (Kane OneDeck / ChatGPT) + handbook de
                    habilidades. Sin RoleGuard a proposito: lo ven los cuatro
                    roles y el handbook filtra por profile.role. */}
                <Route path="/agente" element={<PrivateLayout />}>
                  <Route index element={<AgenteConexion />} />
                  <Route path="habilidades" element={<AgenteHandbook />} />
                </Route>

                {/* Project Management Routes - Unified View */}
                <Route path="/proyectos" element={<PrivateLayout />}>
                  <Route index element={<ProjectsUnifiedView />} />
                  {/*
                    CREAR PROYECTO ES ADMIN-ONLY, Y LA PANTALLA NO LO DECÍA.

                    Al técnico lo frenaba el redirect de `PrivateLayout` (manda
                    todo lo que no esté en `technicianAllowedPaths` a
                    /tecnico/dashboard) — pero ese redirect es SOLO para
                    técnicos. El supervisor y el cliente llegaban a la pantalla,
                    llenaban el formulario entero y recién ahí la base los
                    rechazaba con un 42501 de RLS, sin explicación.

                    Medido contra producción (2026-07-29): `projects` no tiene
                    NINGUNA policy de INSERT — ni permissive ni restrictive —
                    fuera de `admin_all_projects` / `admin_full_access_projects`
                    (`ALL`, `is_admin()`). O sea que para cualquier rol que no
                    sea admin el formulario está garantizado a fallar al enviar.

                    Coincide con la matriz canónica
                    (wiki/pages/role-ui-matrix.md, "Crear proyecto":
                    Admin ✅ / Supervisor ❌ / Líder ❌ / Cliente ❌) y con el
                    registry del agente, donde `create_project` quedó
                    `roles: ['admin']` en la auditoría del 2026-07-30. Este
                    guard es lo que faltaba para que los tres carriles digan lo
                    mismo.

                    Fijado por `src/__tests__/unit/rutaProyectoNuevoAdminOnly.test.js`.
                  */}
                  <Route path="nuevo" element={<RoleGuard allowedRoles={['admin']}><ProjectForm /></RoleGuard>} />
                  <Route path=":id" element={<ProjectDetail />} />
                  {/*
                    `:id/editar` NO lleva el mismo guard a propósito: editar un
                    proyecto SÍ está concedido más allá del admin
                    (`projects_ceiling_update` deja pasar a quien lo creó, a
                    quien lidera el proyecto y al supervisor, mientras no esté
                    `closed`), y `update_project` sigue abierto al técnico en el
                    registry por esa misma razón. Cerrarlo acá sería un techo de
                    más.
                  */}
                  <Route path=":id/editar" element={<ProjectForm />} />
                </Route>

                {/* Client Management Routes — admin only (issue #132) */}
                <Route path="/clientes" element={<PrivateLayout />}>
                  <Route index element={<RoleGuard allowedRoles={['admin']}><ClientList /></RoleGuard>} />
                  <Route path="nuevo" element={<RoleGuard allowedRoles={['admin']}><ClientForm /></RoleGuard>} />
                  <Route path=":id" element={<RoleGuard allowedRoles={['admin']}><ClientDetail /></RoleGuard>} />
                  <Route path=":id/editar" element={<RoleGuard allowedRoles={['admin']}><ClientForm /></RoleGuard>} />
                </Route>

                {/* Expense Routes — admin only (issue #132) */}
                {/*
                  El supervisor entra a ver y a REGISTRAR, no a editar. Los tres
                  guards eran `admin` y la base decía otra cosa: hay
                  `supervisor_view_expenses (SELECT)` y `supervisor_create_expenses
                  (INSERT)` en producción, y la matriz dice "Ver todos los gastos:
                  Supervisor 👁" y "Registrar gasto contra proyecto: ✅".

                  El asimétrico es deliberado y también sale de la matriz:
                  "Editar gasto: Supervisor ❌" y "Eliminar gasto: ❌". El
                  supervisor CARGA gastos pero no los corrige — para eso está el
                  admin. El techo `expenses_ceiling_update` ya lo respalda del
                  lado de la base: sólo puede editar lo que él mismo creó.

                  Es el dato con el que decide compras, así que negárselo no era
                  una precaución: era dejarlo sin la mitad de su trabajo.
                */}
                <Route path="/gastos" element={<PrivateLayout />}>
                  <Route index element={<RoleGuard allowedRoles={['admin', 'supervisor']}><ExpenseList /></RoleGuard>} />
                  <Route path="nuevo" element={<RoleGuard allowedRoles={['admin', 'supervisor']}><ExpenseForm /></RoleGuard>} />
                  <Route path=":id/editar" element={<RoleGuard allowedRoles={['admin']}><ExpenseForm /></RoleGuard>} />
                </Route>

                {/* Tool Management Routes (#86, #91, #95, #105)
                 *
                 * `RoleGuard` agregado en la auditoría de roles del 2026-07-29.
                 * Antes estas rutas colgaban de `PrivateLayout` a secas, o sea
                 * CUALQUIER usuario autenticado — el CLIENTE incluido. Un
                 * cliente logueado que escribía /herramientas en la barra veía
                 * el inventario físico completo de ProMedic (39 herramientas
                 * medidas en producción), el dashboard y la bodega. No es su
                 * proyecto: es la lista de bienes de la empresa.
                 *
                 * El otro lado del mismo agujero era la RLS
                 * (`tools_select qual = true`) y se cierra en
                 * `supabase/migrations/20260730400000_techo_herramientas_sin_cliente.sql`.
                 * Los dos hacen falta: sin el guard el cliente ve la pantalla,
                 * sin el techo la ve igual pidiéndole a PostgREST con su JWT.
                 *
                 * El técnico SÍ entra: la custodia de herramientas es su
                 * trabajo (/mis-herramientas, /bodega, solicitudes).
                 */}
                <Route path="/herramientas" element={<PrivateLayout />}>
                  <Route index element={<RoleGuard allowedRoles={['admin', 'supervisor', 'technician']}><ToolList /></RoleGuard>} />
                  <Route path="categorias" element={<RoleGuard allowedRoles={['admin', 'supervisor', 'technician']}><ToolCategoriesPage /></RoleGuard>} />
                  <Route path="dashboard" element={<RoleGuard allowedRoles={['admin', 'supervisor', 'technician']}><ToolsDashboardPage /></RoleGuard>} />
                  <Route path="mis-solicitudes" element={<RoleGuard allowedRoles={['admin', 'supervisor', 'technician']}><MyToolRequests /></RoleGuard>} />
                  {/*
                    Bandeja de aprobacion (L13). Es el destino de los 10.845
                    avisos que van al supervisor (`tool_request_created` +
                    `tool_request_cancelled`, medidos en prod el 2026-07-30) y
                    que hasta ahora apuntaban a esta misma URL sin que la ruta
                    existiera: `:id` la matcheaba, `ToolDetail` pedia una
                    herramienta con id="solicitudes" y el supervisor recibia un
                    falso "Acceso restringido a administradores".

                    Va ANTES de `:id` a proposito. React Router v6 rankea por
                    especificidad (un segmento estatico le gana a uno dinamico),
                    asi que el orden no cambia el match — pero se lee por orden,
                    y este bloque ya se comio una ruta inexistente durante meses
                    sin que nadie lo notara.

                    #550 — RoleGuard ADMIN. Era admin+supervisor, con el argumento
                    de que son los mismos que `getSupervisorIds()` notifica y los
                    unicos que la policy `supervisor_update_tool_requests` deja
                    hacer UPDATE. Luis fijo "solo los admin pueden aprobar cosas":
                    `approve_tool_request` / `reject_tool_request` son admin-only,
                    y esta bandeja no tiene mas acciones que aprobar y rechazar.
                    Dejarsela al supervisor era ofrecerle una cola que ya no puede
                    accionar — el mismo falso permiso que este archivo viene
                    quitando en otras pantallas.

                    OJO: la policy `supervisor_update_tool_requests` SIGUE
                    concediendo el UPDATE en la base. Esto es pintura; el cierre
                    de la RLS va aparte porque un REVOKE pelado rompe caminos que
                    el supervisor si conserva.
                  */}
                  <Route path="solicitudes" element={<RoleGuard allowedRoles={['admin']}><ToolRequestsInbox /></RoleGuard>} />
                  <Route path=":id" element={<RoleGuard allowedRoles={['admin', 'supervisor', 'technician']}><ToolDetail /></RoleGuard>} />
                </Route>

                <Route path="/mis-herramientas" element={<PrivateLayout />}>
                  <Route index element={<RoleGuard allowedRoles={['admin', 'supervisor', 'technician']}><MyToolsPage /></RoleGuard>} />
                </Route>

                {/* Self-service warehouse pickup (#98) */}
                <Route path="/bodega" element={<PrivateLayout />}>
                  <Route index element={<RoleGuard allowedRoles={['admin', 'supervisor', 'technician']}><WarehousePage /></RoleGuard>} />
                </Route>

                {/* Inventory Routes — admin only (issue #132) */}
                <Route path="/inventario" element={<PrivateLayout />}>
                  <Route index element={<RoleGuard allowedRoles={['admin']}><InventoryList /></RoleGuard>} />
                  <Route path="valuacion" element={<RoleGuard allowedRoles={['admin']}><InventoryValuation /></RoleGuard>} />
                  <Route path="asignaciones" element={<RoleGuard allowedRoles={['admin']}><ToolAssignments /></RoleGuard>} />
                  {/* #798 — el SUPERVISOR da de alta materiales (decision de Luis;
                      la base: policy `supervisor_insert_inventory`). Se abre SOLO
                      esta ruta: listar, editar, lotes y valuacion siguen admin-only
                      y estan pendientes de decision. El tecnico NO entra. */}
                  <Route path="nuevo" element={<RoleGuard allowedRoles={['admin', 'supervisor']}><MaterialForm /></RoleGuard>} />
                  <Route path=":id/editar" element={<RoleGuard allowedRoles={['admin']}><MaterialForm /></RoleGuard>} />
                  <Route path=":id/lotes" element={<RoleGuard allowedRoles={['admin']}><BatchList /></RoleGuard>} />
                  <Route path=":id/assets" element={<RoleGuard allowedRoles={['admin']}><ToolAssetsManager /></RoleGuard>} />
                </Route>

                {/* Admin Routes — admin only (locations + admin inbox).
                    solicitudes-materiales era tambien del supervisor (#132);
                    vuelve a admin-only con #550 — aprobar/rechazar una solicitud
                    de adicionales es del admin y esta bandeja no hace otra cosa. */}
                <Route path="/admin" element={<PrivateLayout />}>
                  <Route path="solicitudes-materiales" element={<RoleGuard allowedRoles={['admin']}><MaterialRequestsInbox /></RoleGuard>} />
                  {/* "Nueva solicitud" no puede vivir en /tecnico/materiales/smart para un
                      admin: TechnicianLayout redirige a TODO admin a /dashboard antes de
                      que la pantalla llegue a montarse. Se monta acá, bajo el mismo guard
                      que la bandeja. */}
                  <Route path="solicitudes-materiales/nueva" element={<RoleGuard allowedRoles={['admin']}><SmartMaterialRequest /></RoleGuard>} />
                  {/* /admin/mensajes retired in #448 — it was an orphaned second
                      messaging UI (0 nav entries) whose own sidebar navigated to
                      /admin/mensajes/:id, a route that never existed here (404 on
                      reload). Its exclusive features (mute, delete-from-list,
                      link-to-bitacora) were ported into /mensajes. */}
                  <Route path="locations" element={<RoleGuard allowedRoles={['admin']}><LocationsPage /></RoleGuard>} />
                  {/* Kane Atlas — SOLO admin. El grafo estatico sale de GET /api/atlas/graph,
                      que ademas exige rol admin en el servidor (ver mcp-server/src/routes/atlas.js). */}
                  <Route path="atlas" element={<RoleGuard allowedRoles={['admin']}><Suspense fallback={<LoadingFallback />}><AtlasPage /></Suspense></RoleGuard>} />
                </Route>

                {/* Supplier Routes — admin only (issue #132) */}
                <Route path="/proveedores" element={<PrivateLayout />}>
                  <Route index element={<RoleGuard allowedRoles={['admin']}><SupplierList /></RoleGuard>} />
                  <Route path="nuevo" element={<RoleGuard allowedRoles={['admin']}><SupplierForm /></RoleGuard>} />
                  <Route path=":id/editar" element={<RoleGuard allowedRoles={['admin']}><SupplierForm /></RoleGuard>} />
                </Route>

                {/* Opportunity Routes — admin only (issue #132) */}
                <Route path="/oportunidades" element={<PrivateLayout />}>
                  <Route index element={<RoleGuard allowedRoles={['admin']}><OpportunityList /></RoleGuard>} />
                  <Route path="nuevo" element={<RoleGuard allowedRoles={['admin']}><OpportunityForm /></RoleGuard>} />
                  <Route path=":id" element={<RoleGuard allowedRoles={['admin']}><OpportunityDetail /></RoleGuard>} />
                  <Route path=":id/editar" element={<RoleGuard allowedRoles={['admin']}><OpportunityForm /></RoleGuard>} />
                </Route>

                {/* Purchase Routes — admin only (issue #132) */}
                <Route path="/compras" element={<PrivateLayout />}>
                  <Route index element={<RoleGuard allowedRoles={['admin']}><PurchaseList /></RoleGuard>} />
                  {/* #550 — vuelve a admin-only. Esta ruta se había abierto a
                      supervisor/technician SOLO para que un líder con la
                      aprobación delegada por proyecto pudiera llegar a la
                      compuerta fina. Luis retiró la delegación ("solo los
                      admin pueden aprobar cosas"), así que ya no hay nadie
                      que dejar pasar y la compuerta gruesa ES la regla. */}
                  <Route path="inbox" element={<RoleGuard allowedRoles={['admin']}><AdminPOInbox /></RoleGuard>} />
                  <Route path="nueva" element={<RoleGuard allowedRoles={['admin']}><PurchaseForm /></RoleGuard>} />
                  {/* #550 — igual que /compras/inbox: se había abierto solo
                      por el líder delegado (que con RoleGuard admin-only era
                      expulsado a /dashboard sin mensaje al tocar una fila del
                      inbox). Retirada la delegación, vuelve a admin-only. */}
                  <Route path=":id" element={<RoleGuard allowedRoles={['admin']}><PurchaseDetail /></RoleGuard>} />
                  <Route path=":id/editar" element={<RoleGuard allowedRoles={['admin']}><PurchaseForm /></RoleGuard>} />
                </Route>

                {/* RFQ Routes — Solicitudes de Cotización (admin + supervisor) */}
                <Route path="/solicitudes-cotizacion" element={<PrivateLayout />}>
                  <Route index element={<RoleGuard allowedRoles={['admin', 'supervisor']}><RfqListPage /></RoleGuard>} />
                  <Route path="nueva" element={<RoleGuard allowedRoles={['admin', 'supervisor']}><RfqBuilder /></RoleGuard>} />
                  <Route path=":id" element={<RoleGuard allowedRoles={['admin', 'supervisor']}><RfqDetailPage /></RoleGuard>} />
                  <Route path=":id/editar" element={<RoleGuard allowedRoles={['admin', 'supervisor']}><RfqBuilder /></RoleGuard>} />
                </Route>

                {/* Progress Routes */}
                <Route path="/progreso" element={<PrivateLayout />}>
                  <Route index element={<ProgressUpload />} />
                  <Route path="galeria" element={<PhotoGallery />} />
                  <Route path="galeria/:projectId" element={<PhotoGallery />} />
                </Route>

                {/* Task Routes */}
                <Route path="/tareas" element={<PrivateLayout />}>
                  <Route
                    index
                    element={
                      <Suspense fallback={<LoadingFallback />}>
                        <TasksPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="list"
                    element={
                      <Suspense fallback={<LoadingFallback />}>
                        <TasksPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="cards"
                    element={
                      <Suspense fallback={<LoadingFallback />}>
                        <TasksPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="kanban"
                    element={
                      <Suspense fallback={<LoadingFallback />}>
                        <TasksPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="timeline"
                    element={
                      <Suspense fallback={<LoadingFallback />}>
                        <TasksPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path=":taskId"
                    element={
                      <Suspense fallback={<LoadingFallback />}>
                        <TaskDetailPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path=":taskId/editar"
                    element={
                      <Suspense fallback={<LoadingFallback />}>
                        <TaskForm />
                      </Suspense>
                    }
                  />
                  <Route
                    path="nuevo"
                    element={
                      <Suspense fallback={<LoadingFallback />}>
                        <TaskForm />
                      </Suspense>
                    }
                  />
                </Route>

                {/* Unified Calendar Routes (#465) — tasks + milestones + payments + project bands */}
                <Route path="/calendario" element={<PrivateLayout />}>
                  <Route
                    index
                    element={
                      <Suspense fallback={<LoadingFallback />}>
                        <CalendarPage />
                      </Suspense>
                    }
                  />
                </Route>

                {/* Invoice Routes */}
                <Route path="/facturas" element={<PrivateLayout />}>
                  <Route index element={<InvoiceListPage />} />
                </Route>

                {/* Unified Messages (Slack-style) Routes */}
                <Route path="/mensajes" element={<PrivateLayout />}>
                  <Route index element={<MessagesPage />} />
                  <Route path=":conversationId" element={<MessagesPage />} />
                </Route>

                {/* User Management Routes — admin only (issue #132) */}
                <Route path="/usuarios" element={<PrivateLayout />}>
                  <Route index element={<RoleGuard allowedRoles={['admin']}><UserList /></RoleGuard>} />
                  <Route path="nuevo" element={<RoleGuard allowedRoles={['admin']}><UserForm /></RoleGuard>} />
                  <Route path=":id/editar" element={<RoleGuard allowedRoles={['admin']}><UserForm /></RoleGuard>} />
                </Route>

                {/* Quotation Routes */}
                <Route path="/cotizaciones" element={<PrivateLayout />}>
                  <Route index element={<QuotationList />} />
                  <Route path="nueva" element={<QuotationBuilder />} />
                  <Route path=":id" element={<QuotationBuilder />} />
                  <Route path=":id/editar" element={<QuotationBuilder />} />
                </Route>

                {/* "My Quotations" — Líder inbox (issue #131) */}
                <Route path="/mis-cotizaciones" element={<PrivateLayout />}>
                  <Route index element={<MyQuotations />} />
                </Route>

                {/* Material Requests Inbox — admin + supervisor.
                    Esta ruta estaba SIN RoleGuard mientras su gemela
                    /admin/solicitudes-materiales sí lo tenía: cualquier
                    usuario autenticado (técnico o cliente) podía abrir la
                    bandeja de aprobación y apretar "Aprobar". Solo RLS
                    frenaba la escritura, y la falla era silenciosa —
                    la solicitud igual quedaba marcada como revisada. */}
                <Route path="/solicitudes-materiales" element={<PrivateLayout />}>
                  {/* #550: admin-only. Medido con el JWT del supervisor contra
                      LOCAL antes de tocarlo, un UPDATE directo a
                      `material_requests` le devolvia 1 fila escrita — o sea que
                      aca no habia falso permiso denegado sino lo contrario: la
                      bandeja le funcionaba de verdad. */}
                  <Route index element={<RoleGuard allowedRoles={['admin']}><MaterialRequestsInbox /></RoleGuard>} />
                </Route>

                {/* Technician Portal Routes */}
                <Route path="/tecnico" element={<TechnicianLayout />}>
                  <Route path="dashboard" element={<TechnicianDashboard />} />
                  <Route path="proyectos" element={<TechnicianProjects />} />
                  <Route path="proyectos/:id" element={<TechnicianProjectDetail />} />
                  <Route path="tareas" element={<TechnicianTasks />} />
                  <Route path="tareas/nueva" element={<Suspense fallback={<LoadingFallback />}><TaskForm /></Suspense>} />
                  <Route path="tareas/:id" element={<TechnicianTaskDetail />} />
                  <Route path="tareas/:id/editar" element={<Suspense fallback={<LoadingFallback />}><TaskForm /></Suspense>} />
                  <Route path="progreso" element={<ProgressUpload />} />
                  <Route path="materiales" element={<TechnicianMaterials />} />
                  <Route path="materiales/solicitar" element={<MaterialRequestForm />} />
                  <Route path="materiales/smart" element={<SmartMaterialRequest />} />
                  {/* Legacy mobile portal route — redirect to the unified /mis-herramientas (#93 rescope) */}
                  <Route path="herramientas" element={<Navigate to="/mis-herramientas" replace />} />
                  <Route path="horas" element={<TimeEntryPage />} />
                  <Route path="incidentes" element={<TechnicianIncidents />} />
                  <Route path="incidentes/nuevo" element={<IncidentReportForm />} />
                  <Route path="mensajes" element={<TechnicianMessages />} />
                  <Route path="mensajes/:conversationId" element={<ProjectChat />} />
                  <Route path="checklists/:projectId" element={<ChecklistView />} />
                  <Route path="checklists/:checklistId/detalle" element={<ChecklistDetail />} />
                  <Route path="firmas/:projectId" element={<SignatureList />} />
                  <Route path="proyectos/:projectId/firma" element={<SignatureCapture />} />
                  <Route path="perfil" element={<TechnicianProfile />} />
                  <Route index element={<Navigate to="/tecnico/dashboard" replace />} />
                </Route>

                {/* 404 Catch-all — must be last */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </ChatProvider>
            </NotificationProvider>
          </Router>
        </AuthProvider>
      </QueryClientProvider>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </ErrorBoundary>
  );
}

export default App; 